import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface TrialExpiredProps {
  open: boolean;
  onClose?: () => void;
}

const TrialExpired: React.FC<TrialExpiredProps> = ({ open, onClose }) => {
  const navigate = useNavigate();

  const handleViewPricing = () => {
    navigate("/landing/pricing");
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleEscapeKey = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          border: "1px solid #e2e0d8",
          maxWidth: "440px",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(3px)",
        },
      }}
      disableEscapeKeyDown
      onBackdropClick={handleBackdropClick}
      onKeyDown={handleEscapeKey}
    >
      {/* Top accent bar */}
      <Box
        sx={{
          height: "5px",
          background: "linear-gradient(90deg, #e24b4a, #c0392b)",
        }}
      />

      <DialogContent sx={{ padding: "30px 28px 0", textAlign: "center" }}>
        {/* Icon circle */}
        <Box
          sx={{
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            background: "#fcebeb",
            border: "1px solid #f7c1c1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="#a32d2d"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: "28px", height: "28px" }}
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </Box>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            fontSize: "19px",
            fontWeight: 700,
            color: "#1a1a18",
            marginBottom: "12px",
            lineHeight: 1.35,
          }}
        >
          Your free trial has ended
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            color: "#5f5e5a",
            lineHeight: 1.75,
            marginBottom: 0,
          }}
        >
          Your 14-day free trial has expired. To continue using the platform and
          access all features, please upgrade to a paid plan.
        </Typography>

        {/* Trial badge */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            background: "#fcebeb",
            border: "1px solid #f7c1c1",
            color: "#791f1f",
            fontSize: "13px",
            fontWeight: 600,
            padding: "6px 14px",
            borderRadius: "20px",
            margin: "18px auto 0",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: "14px", height: "14px" }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          14-day trial expired
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "24px 28px 28px",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={handleViewPricing}
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: "#ffffff",
            background: "#BA7517",
            border: "none",
            borderRadius: "9px",
            padding: "12px 0",
            width: "100%",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            transition: "background 0.15s",
            "&:hover": {
              background: "#854F0B",
            },
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: "17px", height: "17px" }}
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
          View pricing plans
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrialExpired;
